import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, ArrowRight, Zap, Briefcase, Users, ChevronDown, BarChart, Info, DollarSign } from 'lucide-react';

export default function PricingCalculator() {
  // Company profile state
  const [companySize, setCompanySize] = useState(50);
  const [companyType, setCompanyType] = useState('startup');
  const [fundingStage, setFundingStage] = useState('seed');
  const [industry, setIndustry] = useState('tech');
  
  // Team structure state
  const [recruitersCount, setRecruitersCount] = useState(1);
  const [hrCount, setHrCount] = useState(1);
  const [hiringManagersCount, setHiringManagersCount] = useState(3);
  
  // Current tools state with monthly costs
  const [currentTools, setCurrentTools] = useState({
    ats: { active: false, name: "", cost: 0 },
    crm: { active: false, name: "", cost: 0 },
    jobBoards: { active: false, name: "", cost: 0 },
    agencies: { active: false, name: "", cost: 0 },
    sourcing: { active: false, name: "", cost: 0 },
    scheduling: { active: false, name: "", cost: 0 },
    interviewTools: { active: false, name: "", cost: 0 }
  });
  
  // Hiring needs state
  const [hiringFrequency, setHiringFrequency] = useState('moderate');
  const [rolesPerMonth, setRolesPerMonth] = useState(5);
  const [timeToHire, setTimeToHire] = useState('standard');
  const [interviewRounds, setInterviewRounds] = useState(3);
  const [futureHiringPlans, setFutureHiringPlans] = useState('steady');
  
  // Current costs and metrics state
  const [currentCosts, setCurrentCosts] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [yearlySavings, setYearlySavings] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [metrics, setMetrics] = useState({
    costPerHire: 0,
    timeToHire: 0,
    efficiency: 0,
    quality: 0,
    potential: {
      costPerHire: 0,
      timeToHire: 0,
      efficiency: 0,
      quality: 0
    }
  });

  // Tool examples and their typical costs
  const toolExamples = {
    ats: ['Greenhouse', 'Lever', 'Workday', 'Dover', 'Ashby', 'BambooHR', 'Custom'],
    crm: ['Beamery', 'Gem', 'Talemetry', 'Yello', 'Avature', 'Custom'],
    jobBoards: ['LinkedIn', 'Indeed', 'ZipRecruiter', 'Monster', 'Glassdoor', 'Custom'],
    agencies: ['Robert Half', 'Kforce', 'TEKsystems', 'Randstad', 'Local Agency', 'Custom'],
    sourcing: ['LinkedIn Recruiter', 'Gem AI', 'SeekOut', 'HireEZ', 'Entelo', 'Custom'],
    scheduling: ['Calendly', 'GoodTime', 'Clara', 'Prelude', 'Candidates.fyi', 'Custom'],
    interviewTools: ['CoderPad', 'HackerRank', 'Karat', 'BrightHire', 'Metaview', 'Custom']
  };

  // Calculate total current costs
  useEffect(() => {
    let total = 0;
    Object.values(currentTools).forEach(tool => {
      if (tool.active) {
        total += Number(tool.cost) || 0;
      }
    });
    setCurrentCosts(total);
    
    // Update cost-per-hire metric
    if (rolesPerMonth > 0) {
      const costPerHire = Math.round(total / rolesPerMonth);
      setMetrics(prev => ({
        ...prev,
        costPerHire: costPerHire,
        potential: {
          ...prev.potential,
          costPerHire: Math.round(calculatedPrice / rolesPerMonth)
        }
      }));
    }
  }, [currentTools, rolesPerMonth, calculatedPrice]);

  // Update time-to-hire metric
  useEffect(() => {
    let weeks = 0;
    switch (timeToHire) {
      case 'fast': weeks = 2.5; break;
      case 'standard': weeks = 4.5; break;
      case 'extended': weeks = 8; break;
      default: weeks = 4.5;
    }
    // Add 0.5 weeks per additional interview round beyond 3
    weeks += Math.max(0, (interviewRounds - 3) * 0.5);
    
    setMetrics(prev => ({
      ...prev,
      timeToHire: weeks,
      potential: {
        ...prev.potential,
        timeToHire: Math.max(1.5, weeks * 0.7) // 30% faster time-to-hire with our solution
      }
    }));
  }, [timeToHire, interviewRounds]);

  // Update efficiency metric (0-100%)
  useEffect(() => {
    // Calculate efficiency based on tools integration and process
    const toolCount = Object.values(currentTools).filter(t => t.active).length;
    const toolComplexityFactor = 1 - Math.min(0.4, toolCount * 0.05); // 5% efficiency loss per tool
    const processFactor = 1 - Math.min(0.5, (interviewRounds - 2) * 0.1); // 10% efficiency loss per interview round beyond 2
    
    const efficiency = Math.round(toolComplexityFactor * processFactor * 100);
    
    setMetrics(prev => ({
      ...prev,
      efficiency: efficiency,
      potential: {
        ...prev.potential,
        efficiency: Math.min(98, efficiency + 20) // Up to 20% efficiency gain with our solution
      }
    }));
  }, [currentTools, interviewRounds]);

  // Update quality metric (0-100%)
  useEffect(() => {
    // Base quality score
    let quality = 70;
    
    // Add quality points for good processes
    if (interviewRounds >= 3) quality += 5;
    if (timeToHire !== 'extended') quality += 5;
    
    // Subtract quality points for process issues
    const toolCount = Object.values(currentTools).filter(t => t.active).length;
    if (toolCount > 4) quality -= (toolCount - 4) * 2; // Too many tools hurts quality
    if (interviewRounds > 5) quality -= (interviewRounds - 5) * 5; // Too many rounds hurts quality
    
    setMetrics(prev => ({
      ...prev,
      quality: Math.min(100, Math.max(40, quality)),
      potential: {
        ...prev.potential,
        quality: Math.min(98, quality + 15) // Up to 15% quality gain with our solution
      }
    }));
  }, [currentTools, interviewRounds, timeToHire]);

  // Handle tool selection toggle
  const toggleTool = (toolKey: string) => {
    setCurrentTools(prev => ({
      ...prev,
      [toolKey]: {
        ...prev[toolKey],
        active: !prev[toolKey].active
      }
    }));
  };

  // Handle tool name change
  const handleToolNameChange = (toolKey: string, name: string) => {
    setCurrentTools(prev => ({
      ...prev,
      [toolKey]: {
        ...prev[toolKey],
        name: name
      }
    }));
  };

  // Handle tool cost change
  const handleToolCostChange = (toolKey: string, cost: string) => {
    setCurrentTools(prev => ({
      ...prev,
      [toolKey]: {
        ...prev[toolKey],
        cost: Number(cost)
      }
    }));
  };

  // Calculate price based on all inputs
  const calculatePrice = () => {
    let basePrice = 2500; // Starting base price
    
    // Company size factors
    if (companySize <= 10) basePrice += 0;
    else if (companySize <= 50) basePrice += 1500;
    else if (companySize <= 200) basePrice += 3000;
    else if (companySize <= 500) basePrice += 5000;
    else basePrice += 8000;
    
    // Company type and funding
    if (companyType === 'startup') {
      if (fundingStage === 'pre-seed') basePrice *= 0.8;
      else if (fundingStage === 'seed') basePrice *= 0.9;
      else if (fundingStage === 'seriesA') basePrice *= 1.0;
      else basePrice *= 1.2;
    } else if (companyType === 'enterprise') {
      basePrice *= 1.3;
    }
    
    // Team size factors
    basePrice += (recruitersCount * 800);
    basePrice += (hrCount * 500);
    basePrice += (hiringManagersCount * 200);
    
    // Hiring velocity factors
    if (hiringFrequency === 'high') basePrice += 2000;
    if (rolesPerMonth > 10) basePrice += 3000;
    if (timeToHire === 'fast') basePrice += 1500;
    if (interviewRounds > 4) basePrice += 1000;
    if (futureHiringPlans === 'aggressive') basePrice += 2500;
    
    setCalculatedPrice(Math.round(basePrice));
    setMonthlySavings(Math.max(0, currentCosts - basePrice));
    setYearlySavings(Math.max(0, (currentCosts - basePrice) * 12));
    setShowResults(true);
    
    // Auto-advance to results tab
    setActiveTab('results');
  };

  // Next tab navigation
  const goToNextTab = () => {
    if (activeTab === 'company') setActiveTab('team');
    else if (activeTab === 'team') setActiveTab('tools');
    else if (activeTab === 'tools') setActiveTab('hiring');
    else if (activeTab === 'hiring') calculatePrice();
  };

  // Previous tab navigation
  const goToPreviousTab = () => {
    if (activeTab === 'team') setActiveTab('company');
    else if (activeTab === 'tools') setActiveTab('team');
    else if (activeTab === 'hiring') setActiveTab('tools');
    else if (activeTab === 'results') setActiveTab('hiring');
  };

  // Component for tool selection with cost inputs
  const ToolCard = ({ toolKey, label, description, icon }: { 
    toolKey: string;
    label: string;
    description: string;
    icon: React.ReactNode;
  }) => {
    const tool = currentTools[toolKey as keyof typeof currentTools];
    
    return (
      <Card className={`transition-all ${tool.active ? 'border-2 border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center h-full">
              <Checkbox 
                id={`tool-${toolKey}`}
                checked={tool.active}
                onCheckedChange={() => toggleTool(toolKey)}
                className="mt-1"
              />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor={`tool-${toolKey}`} className="font-medium cursor-pointer">{label}</Label>
                <div className={`p-1 rounded-full ${tool.active ? 'bg-primary/10' : 'bg-muted'}`}>
                  {icon}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{description}</p>
              
              {tool.active && (
                <div className="mt-4 space-y-3">
                  <div>
                    <Label htmlFor={`${toolKey}-name`} className="block text-sm mb-1">Current Tool/Vendor</Label>
                    <Select value={tool.name} onValueChange={(value) => handleToolNameChange(toolKey, value)}>
                      <SelectTrigger id={`${toolKey}-name`}>
                        <SelectValue placeholder="Select or enter name" />
                      </SelectTrigger>
                      <SelectContent>
                        {toolExamples[toolKey as keyof typeof toolExamples].map(example => (
                          <SelectItem key={example} value={example}>{example}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor={`${toolKey}-cost`} className="block text-sm mb-1">Monthly Cost ($)</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input 
                        id={`${toolKey}-cost`}
                        type="number" 
                        min="0" 
                        placeholder="0"
                        value={tool.cost} 
                        onChange={(e) => handleToolCostChange(toolKey, e.target.value)} 
                        className="flex-1" 
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Function for navigation (replace react-router)
  const navigateTo = (path: string) => {
    // You can implement your navigation logic here
    console.log(`Navigating to: ${path}`);
    // For example: window.location.href = path;
  };

  // Render metrics summary for the current page
  const MetricsSummary = () => {
    return (
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">Running Metrics Summary</h3>
          <Badge className="bg-primary/10 text-primary">Live Preview</Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Current Monthly Cost</p>
            <p className="text-lg font-semibold">${currentCosts}</p>
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-1">Cost Per Hire</p>
            <p className="text-lg font-semibold">${metrics.costPerHire}</p>
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-1">Time to Hire</p>
            <p className="text-lg font-semibold">{metrics.timeToHire.toFixed(1)} weeks</p>
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-1">Hiring Efficiency</p>
            <div className="flex items-center">
              <Progress value={metrics.efficiency} className="h-2 flex-1 mr-2" />
              <span className="text-sm font-medium">{metrics.efficiency}%</span>
            </div>
          </div>
        </div>
        
        {calculatedPrice > 0 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-sm font-medium mb-2">Potential Improvements with Our Solution:</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="text-sm">Save ${monthlySavings}/mo</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-sm">{Math.round((metrics.potential.efficiency - metrics.efficiency))}% efficiency gain</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container py-12 px-4 md:px-6 bg-gradient-to-b from-white to-muted/50">
      <div className="mx-auto text-center max-w-4xl mb-12">
        <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary">Tailored Pricing</Badge>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Your Company Is Unique. Your Hiring Solution Should Be Too.</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Discover how our AI-powered platform can replace your fragmented hiring stack with one solution tailored to your specific needs and team structure.
        </p>
      </div>
      
      <Card className="max-w-4xl mx-auto shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl">Custom Solution Calculator</CardTitle>
          <CardDescription className="text-base">
            Tell us about your company and current hiring process to see your personalized solution.
          </CardDescription>
        </CardHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6">
            <TabsList className="grid grid-cols-5 mb-6 w-full">
              <TabsTrigger value="company">Company</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
              <TabsTrigger value="tools">Tools</TabsTrigger>
              <TabsTrigger value="hiring">Hiring</TabsTrigger>
              <TabsTrigger value="results">Results</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="company" className="m-0">
            <CardContent className="space-y-6 pt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Company Size</label>
                <Slider 
                  value={[companySize]} 
                  min={1} 
                  max={1000} 
                  step={1} 
                  onValueChange={(val) => setCompanySize(val[0])} 
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Solo</span>
                  <span>10-50</span>
                  <span>51-200</span>
                  <span>201-500</span>
                  <span>500+</span>
                </div>
                <p className="mt-2 text-center font-medium">{companySize} {companySize === 1 ? 'employee' : 'employees'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Company Type</label>
                <Select value={companyType} onValueChange={setCompanyType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="startup">Startup</SelectItem>
                    <SelectItem value="smb">Small/Medium Business</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="nonprofit">Non-Profit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {companyType === 'startup' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Funding Stage</label>
                  <Select value={fundingStage} onValueChange={setFundingStage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select funding stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bootstrapped">Bootstrapped</SelectItem>
                      <SelectItem value="pre-seed">Pre-Seed</SelectItem>
                      <SelectItem value="seed">Seed</SelectItem>
                      <SelectItem value="seriesA">Series A</SelectItem>
                      <SelectItem value="seriesB+">Series B+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Industry</label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tech">Technology</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <MetricsSummary />
            </CardContent>
            <CardFooter className="flex justify-end pt-0 pb-6">
              <Button onClick={goToNextTab} className="w-32">
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </TabsContent>
          
          <TabsContent value="team" className="m-0">
            <CardContent className="space-y-6 pt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Recruiters</label>
                <div className="flex items-center gap-4">
                  <Input 
                    type="number" 
                    min="0" 
                    max="100" 
                    value={recruitersCount} 
                    onChange={(e) => setRecruitersCount(Number(e.target.value))}
                    className="w-24" 
                  />
                  <p className="text-sm text-muted-foreground flex-1">
                    Number of full-time recruiters on your team
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">HR/People Operations</label>
                <div className="flex items-center gap-4">
                  <Input 
                    type="number" 
                    min="0" 
                    max="100" 
                    value={hrCount} 
                    onChange={(e) => setHrCount(Number(e.target.value))}
                    className="w-24" 
                  />
                  <p className="text-sm text-muted-foreground flex-1">
                    Number of HR personnel involved in hiring
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Hiring Managers</label>
                <div className="flex items-center gap-4">
                  <Input 
                    type="number" 
                    min="0" 
                    max="100" 
                    value={hiringManagersCount} 
                    onChange={(e) => setHiringManagersCount(Number(e.target.value))}
                    className="w-24" 
                  />
                  <p className="text-sm text-muted-foreground flex-1">
                    Number of managers who regularly conduct interviews
                  </p>
                </div>
              </div>
              
              <MetricsSummary />
            </CardContent>
            <CardFooter className="flex justify-between pt-0 pb-6">
              <Button variant="outline" onClick={goToPreviousTab}>
                Previous
              </Button>
              <Button onClick={goToNextTab} className="w-32">
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </TabsContent>
          
          <TabsContent value="tools" className="m-0">
            <CardContent className="pt-4">
              <p className="mb-4 text-sm text-muted-foreground">
                Select the hiring tools you're currently using and enter their monthly costs:
              </p>
              
              <div className="grid grid-cols-1 gap-4 mb-6">
                <ToolCard 
                  toolKey="ats" 
                  label="Applicant Tracking System" 
                  description="Greenhouse, Lever, Workday, etc." 
                  icon={<Briefcase className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="crm" 
                  label="Candidate Relationship Management" 
                  description="Beamery, Gem, etc." 
                  icon={<Users className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="jobBoards" 
                  label="Job Boards" 
                  description="Indeed, LinkedIn, etc." 
                  icon={<Briefcase className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="agencies" 
                  label="Recruiting Agencies" 
                  description="External recruiters & agencies" 
                  icon={<Users className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="sourcing" 
                  label="Sourcing Tools" 
                  description="LinkedIn Recruiter, etc." 
                  icon={<Zap className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="scheduling" 
                  label="Scheduling Tools" 
                  description="Calendly, GoodTime, etc." 
                  icon={<Zap className="h-5 w-5" />} 
                />
                
                <ToolCard 
                  toolKey="interviewTools" 
                  label="Interview Tools" 
                  description="CoderPad, HackerRank, etc." 
                  icon={<Zap className="h-5 w-5" />} 
                />
              </div>
              
              <MetricsSummary />
            </CardContent>
            <CardFooter className="flex justify-between pt-4 pb-6">
              <Button variant="outline" onClick={goToPreviousTab}>
                Previous
              </Button>
              <Button onClick={goToNextTab} className="w-32">
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </TabsContent>
          
          <TabsContent value="hiring" className="m-0">
            <CardContent className="space-y-6 pt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hiring Frequency</label>
                <Select value={hiringFrequency} onValueChange={setHiringFrequency}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hiring frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low (Occasional hiring)</SelectItem>
                    <SelectItem value="moderate">Moderate (Regular hiring)</SelectItem>
                    <SelectItem value="high">High (Constant hiring)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Average Open Roles Per Month</label>
                <Input 
                  type="number" 
                  min="1" 
                  max="100" 
                  value={rolesPerMonth} 
                  onChange={(e) => setRolesPerMonth(Number(e.target.value))} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Average Time to Hire</label>
                <Select value={timeToHire} onValueChange={setTimeToHire}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time to hire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fast">Fast (&lt;3 weeks)</SelectItem>
                    <SelectItem value="standard">Standard (3-6 weeks)</SelectItem>
                    <SelectItem value="extended">Extended (&gt;6 weeks)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Average Interview Rounds</label>
                <Input 
                  type="number" 
                  min="1" 
                  max="10" 
                  value={interviewRounds} 
                  onChange={(e) => setInterviewRounds(Number(e.target.value))} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Future Hiring Plans</label>
                <Select value={futureHiringPlans} onValueChange={setFutureHiringPlans}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select future hiring plans" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decreasing">Decreasing</SelectItem>
                    <SelectItem value="steady">Steady</SelectItem>
                    <SelectItem value="moderate-growth">Moderate Growth</SelectItem>
                    <SelectItem value="aggressive">Aggressive Growth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <MetricsSummary />
            </CardContent>
            <CardFooter className="flex justify-between pt-0 pb-6">
              <Button variant="outline" onClick={goToPreviousTab}>
                Previous
              </Button>
              <Button onClick={calculatePrice} className="w-32">
                Calculate <BarChart className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </TabsContent>
          
          <TabsContent value="results" className="m-0">
            <CardContent className="pt-4">
              {showResults ? (
                <div className="space-y-6">
                  <div className="bg-primary/5 border border-primary/10 p-4 rounded-lg mb-6">
                    <div className="flex items-center mb-2">
                      <Info className="h-5 w-5 text-primary mr-2" />
                      <p className="text-primary font-medium">Your solution has been customized based on your inputs</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Our pricing includes all features, unlimited users, and dedicated support. No hidden fees or per-seat pricing.
                    </p>
                  </div>
                
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-muted/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Current Monthly Cost</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold">${currentCosts}</p>
                        <p className="text-sm text-muted-foreground">Your fragmented tools</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-primary/5 border-primary/10">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Your Custom Solution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">${calculatedPrice}</p>
                        <p className="text-sm text-muted-foreground">One unified platform</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-green-50 border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Monthly Savings</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-green-700">${monthlySavings}</p>
                        <p className="text-sm text-muted-foreground">${yearlySavings} per year</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border shadow-sm">
                    <h3 className="text-xl font-bold mb-4">Metrics Comparison</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-medium mb-3">Current Process</h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Cost Per Hire</span>
                              <span className="font-medium">${metrics.costPerHire}</span>
                            </div>
                            <Progress value={100} className="h-2 bg-muted" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Time to Hire</span>
                              <span className="font-medium">{metrics.timeToHire.toFixed(1)} weeks</span>
                            </div>
                            <Progress value={100} className="h-2 bg-muted" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Process Efficiency</span>
                              <span className="font-medium">{metrics.efficiency}%</span>
                            </div>
                            <Progress value={metrics.efficiency} className="h-2" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Hire Quality Score</span>
                              <span className="font-medium">{metrics.quality}%</span>
                            </div>
                            <Progress value={metrics.quality} className="h-2" />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-medium mb-3">With Our Solution</h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Cost Per Hire</span>
                              <div className="flex items-center">
                                <Badge className="bg-green-100 text-green-800 mr-2">
                                  {Math.round((metrics.costPerHire - metrics.potential.costPerHire) / metrics.costPerHire * 100)}% less
                                </Badge>
                                <span className="font-medium">${metrics.potential.costPerHire}</span>
                              </div>
                            </div>
                            <Progress value={metrics.potential.costPerHire/metrics.costPerHire*100} className="h-2 bg-muted" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Time to Hire</span>
                              <div className="flex items-center">
                                <Badge className="bg-green-100 text-green-800 mr-2">
                                  {Math.round((metrics.timeToHire - metrics.potential.timeToHire) / metrics.timeToHire * 100)}% faster
                                </Badge>
                                <span className="font-medium">{metrics.potential.timeToHire.toFixed(1)} weeks</span>
                              </div>
                            </div>
                            <Progress value={metrics.potential.timeToHire/metrics.timeToHire*100} className="h-2 bg-muted" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Process Efficiency</span>
                              <div className="flex items-center">
                                <Badge className="bg-primary/10 text-primary mr-2">
                                  +{metrics.potential.efficiency - metrics.efficiency}%
                                </Badge>
                                <span className="font-medium">{metrics.potential.efficiency}%</span>
                              </div>
                            </div>
                            <Progress value={metrics.potential.efficiency} className="h-2" />
                          </div>
                          
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-muted-foreground">Hire Quality Score</span>
                              <div className="flex items-center">
                                <Badge className="bg-primary/10 text-primary mr-2">
                                  +{metrics.potential.quality - metrics.quality}%
                                </Badge>
                                <span className="font-medium">{metrics.potential.quality}%</span>
                              </div>
                            </div>
                            <Progress value={metrics.potential.quality} className="h-2" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 p-6 rounded-lg">
                    <h3 className="text-xl font-bold mb-4">Your Custom Solution Includes:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>AI-powered candidate sourcing</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Automated interview scheduling</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Integrated ATS functionality</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Smart candidate matching</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Advanced analytics dashboard</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Collaborative hiring tools</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Dedicated support team</p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p>Unlimited job postings</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center pt-4">
                    <Button size="lg" className="w-full md:w-2/3 lg:w-1/2 text-lg" onClick={() => navigateTo('/demo')}>
                      Schedule a Demo
                    </Button>
                    <p className="text-sm text-muted-foreground mt-3">
                      Want to discuss a custom enterprise plan? <Button variant="link" className="p-0 h-auto" onClick={() => navigateTo('/contact')}>Speak with our Founder</Button>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Complete all steps to see your custom solution and pricing</p>
                  <Button 
                    variant="outline" 
                    className="mt-4" 
                    onClick={() => setActiveTab('company')}
                  >
                    Start Calculation
                  </Button>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-start pt-0 pb-6">
              <Button variant="outline" onClick={goToPreviousTab}>
                Back to Hiring Details
              </Button>
            </CardFooter>
          </TabsContent>
        </Tabs>
      </Card>
      
      <div className="mx-auto max-w-4xl mt-16 space-y-12">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Why Choose Our All-In-One Solution?</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Reduce complexity and costs by replacing your fragmented hiring stack with our unified AI-powered platform.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card>
            <CardHeader>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Streamlined Workflow</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Eliminate switching between multiple tools. Manage your entire hiring process in one intuitive platform.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <BarChart className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Cost Efficiency</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Replace multiple subscription fees with one transparent pricing model tailored to your company's specific needs.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>AI-Powered Matching</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Our advanced AI matches the right candidates to your roles, reducing time-to-hire and improving quality of hire.
              </p>
            </CardContent>
          </Card>
        </div>
        
        <div className="bg-muted/50 p-8 rounded-xl">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-2">Ready to Transform Your Hiring?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join forward-thinking companies that have streamlined their hiring process with our unified platform.
            </p>
            <Button size="lg" className="text-lg" onClick={() => navigateTo('/register')}>
              Start Free Trial
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}